// CarGurus API Service - Correctly Implemented per Documentation
// Uses POST method as required by CarGurus

import { supabase } from '../lib/supabase';

class CarGurusAPIService {
  constructor() {
    // Your actual CarGurus credentials
    this.credentials = {
      dealer: {
        appId: 'acbbc8e9-fcfd-4bae-ac9a-2e1a2f1b0de2',
        authToken: '33a2e213-3480-4f29-bca2-33d073b46405',
        externalDealerId: 'Paradise-ds' // EXACT case from email
      },
      vin: {
        appId: 'ccaad471-5631-4f91-a810-b80101756c78',
        authToken: '5b44a3dd-c6aa-495f-80d0-92fd286cafae',
        externalDealerId: 'Paradise-dsv' // EXACT case from email
      }
    };

    // CarGurus API endpoints (from documentation)
    this.endpoints = {
      dealerStats: 'https://www.cargurus.com/Cars/api/2.0/dealerStatsRequest.action',
      dealerStatsAggregate: 'https://www.cargurus.com/Cars/api/2.0/dealerStatsAggregateRequest.action',
      vinStatsBasic: 'https://www.cargurus.com/Cars/api/2.0/dealerStatsVinsBasicRequest.action',
      vinStatsAdvanced: 'https://www.cargurus.com/Cars/api/2.0/dealerStatsVinsRequest.action'
    };
  }

  // Format date as YYYY-MM-DD for CarGurus
  formatDate(date) {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Make API request using correct POST format through proxy
  async makeCarGurusRequest(endpoint, appId, authToken, requestBody) {
    try {
      // Determine proxy URL based on environment
      let proxyUrl;
      
      if (window.location.hostname === 'localhost') {
        // Local development - use Vite proxy
        const url = new URL(endpoint);
        const apiPath = url.pathname.replace('/Cars/api/2.0/', '');
        proxyUrl = `/api/cargurus/${apiPath}`;
      } else {
        // Production on Hostinger - use PHP proxy
        proxyUrl = '/api/cargurus.php';
      }
      
      // Build request data
      let requestData;
      let headers;
      
      if (window.location.hostname === 'localhost') {
        // Local dev - send as form data
        const formData = new URLSearchParams();
        formData.append('appId', appId);
        formData.append('authToken', authToken);
        formData.append('body', JSON.stringify(requestBody));
        requestData = formData.toString();
        headers = {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/json'
        };
      } else {
        // Production - send as JSON for PHP
        requestData = JSON.stringify({
          endpoint: endpoint,
          appId: appId,
          authToken: authToken,
          body: requestBody
        });
        headers = {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        };
      }

      console.log('CarGurus API Request via proxy:', {
        proxyUrl,
        appId,
        body: requestBody
      });

      // Make the POST request through proxy
      const response = await fetch(proxyUrl, {
        method: 'POST',
        headers: headers,
        body: requestData
      });

      const responseText = await response.text();
      console.log('CarGurus Raw Response:', responseText);
      console.log('Response Status:', response.status);

      // Try to parse the response
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse CarGurus response:', responseText);
        throw new Error(`Invalid JSON response from CarGurus: ${responseText.substring(0, 200)}`);
      }

      // Check if CarGurus returned an error
      if (data.error) {
        console.error('CarGurus API Error:', data.error);
        throw new Error(`CarGurus Error: ${data.error.description || data.error.error_code || 'Unknown error'}`);
      }

      // If we got here, we have real data!
      console.log('SUCCESS! Got real CarGurus data:', data);
      return {
        success: true,
        data: data,
        isLive: true
      };

    } catch (error) {
      console.error('DETAILED CarGurus API Error:', error.message);
      alert(`CarGurus API Error: ${error.message}`); // Show the actual error
      
      // Return mock data as fallback
      return {
        success: false,
        error: error.message,
        data: this.getMockData(requestBody),
        isLive: false,
        isMock: true
      };
    }
  }

  // Generate mock data for testing
  getMockData(requestBody) {
    const mockData = {
      success: true,
      total_leads: Math.floor(Math.random() * 100),
      total_impressions: Math.floor(Math.random() * 1000),
      total_clicks: Math.floor(Math.random() * 500),
      vin_stats: [
        {
          vin: 'MOCK123456789',
          vdps: Math.floor(Math.random() * 50),
          srps: Math.floor(Math.random() * 100)
        }
      ]
    };
    return mockData;
  }

  // Proxy request through backend to avoid CORS
  async makeProxiedRequest(endpoint, appId, authToken, requestBody) {
    try {
      const response = await fetch('/api/cargurus-proxy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          endpoint,
          appId,
          authToken,
          body: requestBody
        })
      });

      const data = await response.json();
      
      return {
        success: data.success !== false,
        data: data,
        isLive: true
      };

    } catch (error) {
      console.error('Proxy request failed:', error);
      return {
        success: false,
        error: error.message,
        data: null,
        isLive: false
      };
    }
  }

  // Get dealer statistics (daily)
  async getDealerStats(startDate, endDate) {
    const requestBody = {
      external_dealer_id: this.credentials.dealer.externalDealerId,
      start_date: this.formatDate(startDate),
      end_date: this.formatDate(endDate)
    };

    return this.makeCarGurusRequest(
      this.endpoints.dealerStats,
      this.credentials.dealer.appId,
      this.credentials.dealer.authToken,
      requestBody
    );
  }

  // Get aggregated dealer statistics
  async getDealerStatsAggregate(startDate, endDate) {
    const requestBody = {
      external_dealer_id: this.credentials.dealer.externalDealerId,
      start_date: this.formatDate(startDate),
      end_date: this.formatDate(endDate)
    };

    return this.makeCarGurusRequest(
      this.endpoints.dealerStatsAggregate,
      this.credentials.dealer.appId,
      this.credentials.dealer.authToken,
      requestBody
    );
  }

  // Get VIN statistics (basic)
  async getVINStatsBasic(vins, startDate, endDate) {
    const requestBody = {
      external_dealer_id: this.credentials.vin.externalDealerId,
      vins: Array.isArray(vins) ? vins : [vins],
      start_date: this.formatDate(startDate),
      end_date: this.formatDate(endDate)
    };

    return this.makeCarGurusRequest(
      this.endpoints.vinStatsBasic,
      this.credentials.vin.appId,
      this.credentials.vin.authToken,
      requestBody
    );
  }

  // Get VIN statistics (advanced)
  async getVINStatsAdvanced(vins, startDate, endDate) {
    const requestBody = {
      external_dealer_id: this.credentials.vin.externalDealerId,
      vins: Array.isArray(vins) ? vins : [vins],
      start_date: this.formatDate(startDate),
      end_date: this.formatDate(endDate)
    };

    return this.makeCarGurusRequest(
      this.endpoints.vinStatsAdvanced,
      this.credentials.vin.appId,
      this.credentials.vin.authToken,
      requestBody
    );
  }

  // Test API connection
  async testConnection() {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 7); // Last 7 days

    try {
      const result = await this.getDealerStats(startDate, endDate);
      return {
        success: result.success,
        message: result.success 
          ? 'API connection successful! Getting live data.'
          : 'API connection failed. Check credentials or contact support.',
        data: result.data
      };
    } catch (error) {
      return {
        success: false,
        message: 'Connection test failed: ' + error.message
      };
    }
  }
}

// Export singleton
const carGurusAPIService = new CarGurusAPIService();
export default carGurusAPIService;
